/* eslint-disable react-hooks/exhaustive-deps */
import {
  Button,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Flex,
  ModalBody,
  ModalFooter,
  Text,
  useColorMode,
  useColorStyle,
} from '@tonic-one/react';
import CSpinner from '../../../components/CSpinner';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { deleteCookie, getProvisionCookie } from '../../../utils/common';
import { ProvisionApi } from '@Apis';
import { AuthenticationInfoRes } from '../../../types/provision';
import ErrorAlert from '../../../components/Common/ErrorAlert';
import { reportPendo } from '../../../utils/setupPendo';
import {
  GoogleLoginSvg,
  ProvisionOrgInfo,
  StepIcon,
} from '../ProvisionModal/share';
import { ServiceForReAuth } from '@Types/administrator';
import { Service } from '@Types/app';

const GmailReAuthModal = ({
  service,
  orgId,
  authAccount,
  onClose,
}: {
  service: ServiceForReAuth.Gmail | ServiceForReAuth.GmailInline;
  orgId: string;
  authAccount: string;
  onClose: () => void;
}) => {
  const [colorMode] = useColorMode();
  const [colorStyle] = useColorStyle({ colorMode });
  const { t } = useTranslation(['provision', 'common']);
  const [authenticationInfo, setAuthenticationInfo] =
    useState<null | AuthenticationInfoRes>(null);
  const [reAuthDone, setReAuthDone] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const timeRef = useRef<any>(null);
  const cookieName = {
    [Service.Gmail]: 'provision_status_21',
    [Service.GmailInline]: 'provision_status_22',
  }[service];
  const onClick = async () => {
    const authUrl = authenticationInfo?.[0]?.authenticationUrl;
    if (!authUrl) {
      return;
    }
    reportPendo('ReAuth.GrantPermission.Click', {
      orgId,
      service,
    });
    setErrorMsg(undefined);
    setLoading(true);
    window.open(authUrl, '_blank');
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    timeRef.current = setInterval(async () => {
      //{"code":"","state":"","serviceType":"21","status":"success"}
      //{"code":"","state":"","serviceType":"21","status":"failure","error":"780"}
      const gmailProvisionInfo = getProvisionCookie(cookieName);
      if (gmailProvisionInfo) {
        clearInterval(timeRef.current);
        deleteCookie('provision_status_');
        const { error, code } = JSON.parse(gmailProvisionInfo);
        if (error) {
          setErrorMsg(t(`provision.service.gmail.error.deniedGrant`));
        } else {
          try {
            const res = await ProvisionApi.apiSaveReAuthInfo(
              service,
              code,
              authAccount,
              orgId
            );
            if (res.data?.status === 'failure') {
              setErrorMsg(
                t([
                  `provision.service.gmail.error.${res.data?.reason}`,
                  'provision.service.gmail.error.FAILURE;-1',
                ])
              );
            } else {
              setReAuthDone(true);
            }
          } catch (e) {
            setErrorMsg(t('common.apiException.internalError'));
            console.error(JSON.stringify(e));
          }
        }
        setLoading(false);
      }
    }, 1000);
  };
  useEffect(() => {
    if (timeRef.current) {
      clearInterval(timeRef.current);
    }
    return () => {
      if (timeRef.current) {
        clearInterval(timeRef.current);
      }
    };
  }, []);

  useEffect(() => {
    ProvisionApi.apiGetReAuthAuthenticationInfo(service, orgId)
      .then((res) => {
        setAuthenticationInfo(res.data);
      })
      .catch((e) => {
        console.error(e);
        setAuthenticationInfo(null);
      });
  }, []);

  if (!authenticationInfo) {
    return <></>;
  }

  return (
    <>
      {loading && <CSpinner />}
      <Modal isOpen={true} isClosable onClose={onClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {t(`provision.reAuth.service.${service}.title`)}
          </ModalHeader>
          <ModalBody>
            <ProvisionOrgInfo orgId={orgId} />
            {errorMsg && (
              <ErrorAlert
                errorMsg={errorMsg}
                onClose={() => setErrorMsg(undefined)}
              />
            )}

            <Flex gap="20px" my="10px">
              <StepIcon checked={reAuthDone} />
              <Flex direction="column" gap="12px">
                <Text>
                  {t(`provision.reAuth.service.${service}.grantPermission`)}
                </Text>

                {reAuthDone ? (
                  <Text color={colorStyle.color.tertiary}>
                    {t('provision.common.permissionGranted')}
                  </Text>
                ) : (
                  <div>
                    <Button
                      px="0px"
                      borderWidth="0px"
                      variant="ghost"
                      disabled={false}
                      onClick={onClick}
                    >
                      <GoogleLoginSvg disabled={false} />
                    </Button>
                  </div>
                )}
              </Flex>
            </Flex>
          </ModalBody>
          <ModalFooter>
            <Button minWidth="20x" onClick={onClose}>
              {t('common.action.label.close')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default GmailReAuthModal;
